import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Card, CardContent,
  Chip, Avatar,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ConstructionBanner from '../components/ConstructionBanner';
import { CollegeNewsSidebar } from '../components/NewsSidebar';
import NFLScheduleSidebar from '../components/NFLScheduleSidebar';
import StartSitTool from '../components/Fantasy/StartSitTool';
import FantasyProjections from '../components/Dashboard/FantasyProjections';
import WaiverWire from '../components/Fantasy/WaiverWire';
import { useConfig } from '../hooks/useConfig';
import { loadBase, loadPlayerStats } from '../services/espnService';

const SIDEBAR_W = 270;
const NAVBAR_H = 64;
const SUBNAV_H = 48;

const sidebarSx = {
  flexShrink: 0,
  width: SIDEBAR_W,
  position: 'sticky',
  top: NAVBAR_H + SUBNAV_H,
  maxHeight: `calc(100vh - ${NAVBAR_H + SUBNAV_H}px)`,
  overflowY: 'auto',
  backgroundColor: 'transparent',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(245,166,35,0.4)', borderRadius: 2 },
};

const HUB_TOOLS_BASE = [
  { emoji: '⚖️', title: 'Start / Sit', desc: 'Compare two players head-to-head and get a data-driven start/sit recommendation with reasoning.', tabIndex: 1, color: '#2ea84b', featureKey: null, alwaysLive: true },
  { emoji: '🔄', title: 'Waiver Wire', desc: 'Surface high-opportunity players trending up before your league catches on.', tabIndex: 4, color: '#f5a623', featureKey: 'waiverWire', alwaysLive: false },
  { emoji: '📊', title: 'Full Rankings', desc: 'PPR and standard fantasy rankings by position, updated weekly.', tabIndex: 2, color: '#38bdf8', featureKey: null, alwaysLive: true },
  { emoji: '🔮', title: 'Projections', desc: 'Weekly point projections for all skill position players based on matchup and usage.', tabIndex: 3, color: '#a78bfa', featureKey: null, alwaysLive: true },
  { emoji: '🎯', title: 'Target Share', desc: 'Track which receivers are getting the most volume and air yards week over week.', tabIndex: null, color: '#f97316', featureKey: null, alwaysLive: false },
  { emoji: '🩺', title: 'Injury Impact', desc: 'When a player goes down, instantly see who benefits and by how much.', tabIndex: null, color: '#fb7185', featureKey: null, alwaysLive: false },
];

function OverviewTab({ onNavToTab }) {
  const { config } = useConfig();
  const features = config?.site_features ?? {};
  const HUB_TOOLS = HUB_TOOLS_BASE.map(t => ({
    ...t,
    status: (t.alwaysLive || (t.featureKey && features[t.featureKey])) ? 'live' : 'coming soon',
  }));
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>🏆 Fantasy Hub</Typography>
      <Typography variant="body2" sx={{ color: '#9eb8d0', mb: 3, lineHeight: 1.7 }}>Your fantasy command center. Click any tool to jump straight in.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
        {HUB_TOOLS.map((tool) => {
          const isLive = tool.status === 'live';
          return (
            <Card key={tool.title} onClick={() => isLive && tool.tabIndex != null && onNavToTab(tool.tabIndex)}
              sx={{ backgroundColor: 'rgba(16,32,52,0.62)', border: `1px solid ${isLive ? tool.color + '55' : '#1e3448'}`, borderRadius: 2, cursor: isLive ? 'pointer' : 'default', transition: 'all 0.15s', position: 'relative', overflow: 'hidden',
                '&:hover': isLive ? { border: `1px solid ${tool.color}99`, backgroundColor: 'rgba(22,42,66,0.8)', transform: 'translateY(-2px)', boxShadow: `0 4px 20px ${tool.color}22` } : {} }}>
              {isLive && <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${tool.color}, ${tool.color}88)` }} />}
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '1.6rem', lineHeight: 1 }}>{tool.emoji}</Typography>
                  <Chip label={isLive ? '✓ Live' : 'Coming Soon'} size="small"
                    sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, bgcolor: isLive ? 'rgba(46,168,75,0.15)' : 'rgba(255,255,255,0.05)', color: isLive ? '#2ea84b' : '#4a6080', border: `1px solid ${isLive ? '#2ea84b44' : '#1e3448'}` }} />
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: isLive ? '#e8f0f8' : '#6a8aaa' }}>{tool.title}</Typography>
                <Typography variant="caption" sx={{ color: isLive ? '#9eb8d0' : '#3a5070', lineHeight: 1.5, display: 'block' }}>{tool.desc}</Typography>
                {isLive && <Typography variant="caption" sx={{ color: tool.color, fontWeight: 700, mt: 1, display: 'block' }}>Open tool →</Typography>}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}

// ── PPR scoring (Full PPR) ────────────────────────────────────────────────────
function calcPPR(s, pos) {
  if (!s || !s.gamesPlayed) return 0;
  const gp = s.gamesPlayed;
  let raw = 0;
  if (pos === 'QB') {
    raw = (s.passingYards||0)*0.04 + (s.passingTouchdowns||0)*4 + (s.interceptions||0)*(-2)
        + (s.rushingYards||0)*0.1 + (s.rushingTouchdowns||0)*6;
  } else if (pos === 'RB') {
    raw = (s.rushingYards||0)*0.1 + (s.rushingTouchdowns||0)*6
        + (s.receptions||0)*1 + (s.receivingYards||0)*0.1 + (s.receivingTouchdowns||0)*6;
  } else {
    raw = (s.receptions||0)*1 + (s.receivingYards||0)*0.1 + (s.receivingTouchdowns||0)*6;
  }
  return raw / gp;
}

const MIN_GAMES_RANK = { QB: 5, RB: 4, WR: 4, TE: 4 };
const POS_COLORS_RANK = { QB: '#f5a623', RB: '#2ea84b', WR: '#38bdf8', TE: '#a78bfa' };
const RANK_POSITIONS = ['QB', 'RB', 'WR', 'TE'];

function RankingsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [players, setPlayers] = useState({});
  const [stats, setStats]     = useState({});
  const [posTab, setPosTab]   = useState(0);

  useEffect(() => {
    loadBase()
      .then(({ players: p }) => {
        const skill = {};
        for (const pl of Object.values(p)) {
          if (RANK_POSITIONS.includes(pl.posKey)) skill[pl.id] = pl;
        }
        setPlayers(skill);
        return loadPlayerStats(Object.keys(skill));
      })
      .then(s => { setStats(s); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load'); setLoading(false); });
  }, []);

  const ranked = useMemo(() => {
    const pos = RANK_POSITIONS[posTab];
    const minG = MIN_GAMES_RANK[pos] ?? 4;
    return Object.values(players)
      .filter(p => p.posKey === pos && (stats[p.id]?.gamesPlayed ?? 0) >= minG)
      .map(p => ({ ...p, ppr: calcPPR(stats[p.id], pos), gp: stats[p.id]?.gamesPlayed ?? 0 }))
      .sort((a, b) => b.ppr - a.ppr)
      .slice(0, 30);
  }, [players, stats, posTab]);

  const posColor = POS_COLORS_RANK[RANK_POSITIONS[posTab]];

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ color: '#ef4444', fontSize: '0.85rem' }}>{error}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#e8f0f8' }}>
          📊 Fantasy Rankings
        </Typography>
        <Chip label="Full PPR" size="small" sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, color: '#9eb8d0', bgcolor: 'rgba(30,52,72,0.6)' }} />
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: '#4a6080', mb: 2 }}>
        2025 Season · ranked by PPR pts/game · min {MIN_GAMES_RANK[RANK_POSITIONS[posTab]]} games played
      </Typography>

      {/* Position tabs */}
      <Tabs value={posTab} onChange={(_, v) => setPosTab(v)}
        sx={{ mb: 2.5, minHeight: 36, '& .MuiTabs-indicator': { backgroundColor: posColor },
          '& .MuiTab-root': { minHeight: 36, py: 0, px: 1.5, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: '#6a8aa8',
            '&.Mui-selected': { color: posColor } } }}>
        {RANK_POSITIONS.map(p => <Tab key={p} label={p} />)}
      </Tabs>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
          <Box sx={{ width: 18, height: 18, border: `2px solid ${posColor}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#6a8aa8' }}>Loading rankings…</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Column headers */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '28px 1fr 56px 56px 40px', gap: 1, px: 1, pb: 0.5, borderBottom: `1px solid rgba(30,52,72,0.6)` }}>
            {['#', 'Player', 'PPR/G', 'Total', 'GP'].map(h => (
              <Typography key={h} sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#4a6080', textAlign: h === '#' ? 'right' : h === 'PPR/G' || h === 'Total' || h === 'GP' ? 'right' : 'left' }}>{h}</Typography>
            ))}
          </Box>

          {ranked.map((p, idx) => {
            const totalPts = p.ppr * p.gp;
            return (
              <Box key={p.id} sx={{ display: 'grid', gridTemplateColumns: '28px 1fr 56px 56px 40px', gap: 1, px: 1, py: 0.6, borderRadius: 1,
                '&:hover': { backgroundColor: 'rgba(22,42,66,0.55)' },
                backgroundColor: idx % 2 === 0 ? 'transparent' : 'rgba(10,20,34,0.3)' }}>
                {/* Rank */}
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: posColor, textAlign: 'right', lineHeight: '24px' }}>
                  {idx + 1}
                </Typography>
                {/* Name + team */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.55rem', bgcolor: posColor + '22', color: posColor, flexShrink: 0, fontWeight: 700 }}>
                    {p.posKey}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2, color: '#e8f0f8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#4a6080', lineHeight: 1 }}>{p.team}</Typography>
                  </Box>
                </Box>
                {/* PPR/G */}
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#2ea84b', textAlign: 'right', lineHeight: '24px' }}>
                  {p.ppr.toFixed(1)}
                </Typography>
                {/* Season total */}
                <Typography sx={{ fontSize: '0.75rem', color: '#9eb8d0', textAlign: 'right', lineHeight: '24px' }}>
                  {totalPts.toFixed(0)}
                </Typography>
                {/* GP */}
                <Typography sx={{ fontSize: '0.72rem', color: '#4a6080', textAlign: 'right', lineHeight: '24px' }}>
                  {p.gp}
                </Typography>
              </Box>
            );
          })}

          {ranked.length === 0 && (
            <Typography sx={{ color: '#4a6080', fontSize: '0.82rem', py: 4, textAlign: 'center' }}>
              No qualifying players found.
            </Typography>
          )}
        </Box>
      )}

      <Typography sx={{ fontSize: '0.62rem', color: '#2a3c52', mt: 3 }}>
        Scoring: Pass TD 4pt · Pass Yd 0.04 · Rush/Rec TD 6pt · Rush/Rec Yd 0.1 · Rec 1pt (Full PPR) · INT -2pt
      </Typography>
    </Box>
  );
}


function WaiverTab() {
  const { config } = useConfig();
  const isLive = config?.site_features?.waiverWire ?? false;

  if (isLive) return <WaiverWire />;

  // Placeholder — feature disabled in admin
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>🔄 Waiver Wire Targets</Typography>
      <Typography variant="caption" sx={{ color: '#9eb8d0', display: 'block', mb: 2 }}>Coming soon — trending adds ranked by projection, NR score, and Sleeper buzz</Typography>
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} sx={{ mb: 1.5, backgroundColor: 'rgba(16, 32, 52, 0.62)', border: '1px solid #1e3448' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ width: 30, height: 30, bgcolor: 'rgba(245,166,35,0.1)', fontSize: '0.6rem', color: '#4a6080' }}>W{i + 1}</Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ height: 10, bgcolor: 'rgba(245,166,35,0.15)', borderRadius: 1, mb: 0.5, width: '55%' }} />
                <Box sx={{ height: 8, bgcolor: 'rgba(18, 32, 52, 0.80)', borderRadius: 1, width: '35%' }} />
              </Box>
              <Chip label="Soon" size="small" sx={{ fontSize: '0.6rem', height: 20, color: '#4a6080', bgcolor: 'rgba(255,255,255,0.04)' }} />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

function FantasyNewsSidebar() {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#f5a623', letterSpacing: '0.05em', fontSize: '0.72rem' }}>⚡ FANTASY NEWS</Typography>
      <CollegeNewsSidebar hideHeader />
    </Box>
  );
}

const TABS = ['Overview', 'Start / Sit', 'Rankings', 'Projections', 'Waiver Wire'];

function FantasyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = parseInt(searchParams.get('tab') ?? '0', 10);
  const [tab, setTab] = useState(Number.isFinite(tabFromUrl) && tabFromUrl >= 0 && tabFromUrl < TABS.length ? tabFromUrl : 0);

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSearchParams(newTab === 0 ? {} : { tab: String(newTab) });
  };
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'transparent' }}>
      <ConstructionBanner />
      <Navbar />
      <Box sx={{ position: 'sticky', top: NAVBAR_H, zIndex: 1100, backgroundColor: 'rgba(6, 14, 26, 0.88)', borderBottom: '1px solid #1e3448', display: 'flex', alignItems: 'center', px: 2, height: SUBNAV_H, overflowX: 'auto' }}>
        <Tabs value={tab} onChange={(_, v) => handleTabChange(v)} textColor="inherit" indicatorColor="primary"
          sx={{ minHeight: SUBNAV_H, '& .MuiTab-root': { minHeight: SUBNAV_H, py: 0, textTransform: 'none', fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' } }}>
          {TABS.map((t) => <Tab key={t} label={t} />)}
        </Tabs>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ ...sidebarSx, borderRight: '1px solid #1e3448', display: { xs: 'none', lg: 'block' } }}><FantasyNewsSidebar /></Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {tab === 0 && <OverviewTab onNavToTab={handleTabChange} />}
          {tab === 1 && <StartSitTool />}
          {tab === 2 && <RankingsTab />}
          {tab === 3 && <FantasyProjections />}
          {tab === 4 && <WaiverTab />}
        </Box>
        <Box sx={{ ...sidebarSx, borderLeft: '1px solid #1e3448', display: { xs: 'none', lg: 'block' } }}><NFLScheduleSidebar /></Box>
      </Box>
    </Box>
  );
}

export default FantasyPage;
